/**
 * Typed access to the published CUSTOMER docs artifact.
 *
 * `generated/` is written by `context/scripts/publish-guides.mjs` from `docs/guides/*.md`
 * and is committed — see that script for why the strip happens at build time rather than
 * in the browser. Nothing outside this module should import the generated files directly:
 * this is where the untyped JSON becomes a typed shape, so a change to the artifact's
 * layout lands in one file.
 *
 * The lookup logic itself lives in `docs/catalog.ts`, because there is a second artifact
 * with the same shape (`docs/internal.ts`, the staff-only guides behind /internal/docs)
 * and two hand-written copies of the same neighbour/search logic would drift.
 *
 * This module carries METADATA only. The guide bodies live in `docs/bodies` so they stay
 * out of the eager bundle — see the note there.
 */
import { FEATURE_SWITCHES_OFF, type FeatureSwitches } from 'constants/featureSwitches'

import { createDocsCatalog } from './catalog'
import { sortDocCategories } from './categoryOrder'
import { withoutGated } from './featureGate'

import manifest from './generated/search-index.json'

import type { DocEntry, DocsCatalog } from './catalog'

export type { DocCategory, DocEntry, DocHeading } from './catalog'
export { daysSinceReview, isStale } from './catalog'

/**
 * The customer catalog. Exported whole for anything that wants to hand a docs source
 * around (see `docs/sources`); the named bindings below are the historical API and are
 * what most callers use.
 *
 * `categories` is RE-ORDERED here (VRG-227) to match the sidebar + Settings rather than
 * the manifest's baked-in editorial order (publish-guides.mjs's `CATEGORIES` array) — see
 * `docs/categoryOrder.ts`. Nothing else changes: `docs`, `neighbours`, `search` etc. are
 * untouched, so guide-to-guide "next" navigation still follows the manifest's own
 * sequence. Deliberately not applied inside `createDocsCatalog` — that factory is shared
 * with the INTERNAL artifact (`docs/internal.ts`), whose single `internal` category has
 * no sidebar/Settings equivalent to sort against.
 */
const catalog = createDocsCatalog(manifest)
export const CUSTOMER_DOCS_CATALOG: DocsCatalog = {
  ...catalog,
  categories: sortDocCategories(catalog.categories),
}

/** Categories that actually have a published guide, in sidebar-then-Settings order (VRG-227). */
export const DOC_CATEGORIES = CUSTOMER_DOCS_CATALOG.categories

/** Every published guide, ordered by category then within-category order then title. */
export const DOCS = CUSTOMER_DOCS_CATALOG.docs

export const findDoc = CUSTOMER_DOCS_CATALOG.findDoc
export const categoryLabel = CUSTOMER_DOCS_CATALOG.categoryLabel
export const docsInCategory = CUSTOMER_DOCS_CATALOG.docsInCategory
export const docNeighbours = CUSTOMER_DOCS_CATALOG.neighbours
export const searchDocs = CUSTOMER_DOCS_CATALOG.search

/**
 * VRG-680 — the customer catalog as a brand may see it under these launch switches: every
 * guide gated on a switch that is off is gone from the nav, the index, neighbours and search,
 * and its route lands on the same "not found" state as an unpublished guide. Cached per switch
 * state so callers get a stable object. Fails closed — no switches means every gated guide is
 * hidden. The un-gated exports above are the FULL artifact; brand-facing readers use this.
 */
const CATALOG_BY_SWITCHES = new Map<string, DocsCatalog>()
export const customerDocsCatalogFor = (
  switches: Partial<FeatureSwitches> = FEATURE_SWITCHES_OFF,
): DocsCatalog => {
  const key = JSON.stringify({ referrals: switches.referrals === true })
  let gated = CATALOG_BY_SWITCHES.get(key)
  if (!gated) {
    const full = manifest as unknown as { docs: DocEntry[] }
    const docs = withoutGated(full.docs, switches)
    if (docs === full.docs) {
      gated = CUSTOMER_DOCS_CATALOG
    } else {
      const built = createDocsCatalog({ ...full, docs, guideCount: docs.length })
      const used = new Set(docs.map(d => d.category))
      gated = { ...built, categories: sortDocCategories(built.categories.filter(c => used.has(c.id))) }
    }
    CATALOG_BY_SWITCHES.set(key, gated)
  }
  return gated
}
